/**
 * StudentAttachmentCards — student-themed mirror of the Teacher's
 * AttachmentPreviewCard / AttachmentViewerModal pair from
 * `components/teacher/ContentEditor/FrameworkSection.jsx`.
 *
 * The two cousins are functionally identical EXCEPT for the download URL:
 * teachers hit `/api/teacher/studio/attachment/{id}/download` (owner-gated),
 * students hit `/api/student/mydesk/attachment/{id}/download` (enrollment-
 * gated). Keeping a thin wrapper here avoids prop-drilling a urlBuilder
 * through PreviewBodies + every section variant.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronRight, Download, X, FileText, Image as Image2, Film, Music,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ATTACH_THEMES = {
  document: { bg: '#fef3c7', text: '#92400e', ring: '#f59e0b' },
  image:    { bg: '#dbeafe', text: '#1e40af', ring: '#3b82f6' },
  video:    { bg: '#fce7f3', text: '#9d174d', ring: '#ec4899' },
  audio:    { bg: '#ede9fe', text: '#5b21b6', ring: '#8b5cf6' },
};

const AttachIcon = ({ category, className = 'w-5 h-5' }) => {
  if (category === 'image') return <Image2 className={className} />;
  if (category === 'video') return <Film className={className} />;
  if (category === 'audio') return <Music className={className} />;
  return <FileText className={className} />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const buildAttachmentUrl = (attachmentId) => {
  const token = localStorage.getItem('desky_token') || '';
  return `${API}/api/student/mydesk/attachment/${attachmentId}/download?auth=${encodeURIComponent(token)}`;
};

/**
 * Which download endpoint these cards hit.
 *
 * Students get the enrollment-gated `/student/mydesk/...` route (the default).
 * The teacher's Content Editor preview renders this exact component tree, but
 * a teacher is not enrolled in their own class — that route 403s and every
 * attachment renders as "Preview unavailable". Wrapping the preview in this
 * provider swaps in the owner-gated teacher route, so one component serves
 * both surfaces instead of a forked copy that can drift.
 */
export const AttachmentUrlContext = createContext(null);
export const useAttachmentUrl = () => useContext(AttachmentUrlContext) || buildAttachmentUrl;

export function StudentAttachmentPreviewCard({ attachment, onOpen, accent, sectionId }) {
  const theme = ATTACH_THEMES[attachment.category] || ATTACH_THEMES.document;
  const urlFor = useAttachmentUrl();
  const directUrl = urlFor(attachment.id);
  const cat = attachment.category;

  if (cat === 'image') {
    return (
      <figure
        className="rounded-2xl border overflow-hidden shadow-sm"
        style={{ borderColor: `${theme.ring}22`, background: 'var(--st-card)' }}
        data-testid={`pv-attachment-${sectionId}-${attachment.id}`}>
        <button type="button" onClick={onOpen} className="block w-full text-left" aria-label={`Open ${attachment.title}`}>
          <div className="w-full flex items-center justify-center" style={{ minHeight: 180, background: 'var(--st-bg)' }}>
            <img
              src={directUrl}
              alt={attachment.title || 'Attachment'}
              loading="lazy"
              decoding="async"
              className="w-full max-h-[420px] object-contain"
              onError={(e) => {
                const wrap = e.currentTarget.parentElement;
                if (wrap) {
                  wrap.innerHTML = '';
                  wrap.style.minHeight = '120px';
                  wrap.classList.add('text-[12px]', 'italic');
                  wrap.style.color = 'var(--st-mute)';
                  wrap.textContent = 'Preview unavailable';
                }
              }}
            />
          </div>
          <div className="flex items-center gap-2 p-3 border-t" style={{ borderColor: `${theme.ring}22` }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: theme.bg, color: theme.text }}>
              <AttachIcon category={cat} className="w-4 h-4" />
            </div>
            <figcaption className="flex-1 min-w-0">
              <p className="text-[13px] font-bold truncate" style={{ color: 'var(--st-ink)' }}>{attachment.title}</p>
              <p className="text-[10px] font-mono uppercase" style={{ color: 'var(--st-mute)' }}>{attachment.extension} · {formatFileSize(attachment.size)}</p>
            </figcaption>
          </div>
        </button>
      </figure>
    );
  }
  if (cat === 'video') {
    return (
      <div className="rounded-2xl border overflow-hidden shadow-sm" style={{ borderColor: `${theme.ring}22`, background: 'var(--st-card)' }} data-testid={`pv-attachment-${sectionId}-${attachment.id}`}>
        <video src={directUrl} controls preload="metadata" className="w-full block bg-black" style={{ maxHeight: 360 }}>
          Sorry, your browser does not support inline video preview.
        </video>
        <div className="flex items-center gap-2 p-3 border-t" style={{ borderColor: `${theme.ring}22` }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: theme.bg, color: theme.text }}>
            <AttachIcon category={cat} className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold truncate" style={{ color: 'var(--st-ink)' }}>{attachment.title}</p>
            <p className="text-[10px] font-mono uppercase" style={{ color: 'var(--st-mute)' }}>{attachment.extension} · {formatFileSize(attachment.size)}</p>
          </div>
        </div>
      </div>
    );
  }
  if (cat === 'audio') {
    return (
      <div className="rounded-2xl border p-4 shadow-sm" style={{ borderColor: `${theme.ring}22`, background: 'var(--st-card)' }} data-testid={`pv-attachment-${sectionId}-${attachment.id}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: theme.bg, color: theme.text }}>
            <AttachIcon category={cat} className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold truncate" style={{ color: 'var(--st-ink)' }}>{attachment.title}</p>
            <p className="text-[10px] font-mono uppercase" style={{ color: 'var(--st-mute)' }}>{attachment.extension} · {formatFileSize(attachment.size)}</p>
          </div>
        </div>
        <audio src={directUrl} controls preload="metadata" className="w-full">
          Sorry, your browser does not support inline audio preview.
        </audio>
      </div>
    );
  }
  return (
    <button
      onClick={onOpen}
      className="group/att rounded-xl border hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center gap-3 p-3 text-left w-full"
      style={{ borderColor: `${theme.ring}22`, background: 'var(--st-card)' }}
      data-testid={`pv-attachment-${sectionId}-${attachment.id}`}>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: theme.bg, color: theme.text }}>
        <AttachIcon category={attachment.category} className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-bold truncate transition-colors" style={{ color: 'var(--st-ink)' }}>{attachment.title}</p>
        <div className="flex items-center gap-1.5 text-[10px] mt-0.5" style={{ color: 'var(--st-mute)' }}>
          <span className="font-mono uppercase font-bold px-1.5 py-0.5 rounded" style={{ background: `${theme.ring}18`, color: theme.text }}>{attachment.extension}</span>
          <span>·</span>
          <span>{formatFileSize(attachment.size)}</span>
        </div>
      </div>
      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover/att:opacity-100 transition-opacity" style={{ background: `${accent || theme.ring}15`, color: accent || theme.ring }}>
        <ChevronRight className="w-4 h-4" />
      </div>
    </button>
  );
}

export function StudentAttachmentViewerModal({ attachment, onClose }) {
  const urlFor = useAttachmentUrl();
  const [blobUrl, setBlobUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const directUrl = urlFor(attachment.id);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    const load = async () => {
      setLoading(true); setError('');
      try {
        const token = localStorage.getItem('desky_token') || '';
        const res = await fetch(`${API}/api/student/mydesk/attachment/${attachment.id}/download`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed to load file (${res.status})`);
        if (attachment.extension === 'txt' || attachment.extension === 'rtf') {
          const txt = await res.text();
          if (!cancelled) setTextContent(txt);
        } else {
          const blob = await res.blob();
          createdUrl = URL.createObjectURL(blob);
          if (!cancelled) setBlobUrl(createdUrl);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachment.id, attachment.extension]);

  const theme = ATTACH_THEMES[attachment.category] || ATTACH_THEMES.document;

  // Portalled to document.body — nested normally, the page's zoomed <article>
  // ancestor traps this fixed overlay below the app header (see Lightbox in
  // LessonView.jsx for the same fix). Escaping via portal renders it as a
  // true sibling of the header, so z-[80] actually wins.
  return createPortal(
    <div className="fixed inset-0 z-[80] bg-black/75 flex items-center justify-center p-4" onClick={onClose} data-testid="student-attachment-viewer-modal">
      <div className="rounded-2xl shadow-2xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} style={{ background: 'var(--st-card)' }}>
        <div className="flex items-center gap-3 px-5 py-3 border-b" style={{ borderColor: 'var(--st-border)' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: theme.bg, color: theme.text }}>
            <AttachIcon category={attachment.category} className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[14px] font-bold truncate" style={{ color: 'var(--st-ink)' }}>{attachment.title}</p>
            <p className="text-[10px] truncate" style={{ color: 'var(--st-mute)' }}>{attachment.original_filename || ''} · {formatFileSize(attachment.size)} · <span className="uppercase font-mono">{attachment.extension}</span></p>
          </div>
          <a href={directUrl} download={attachment.original_filename || attachment.title} target="_blank" rel="noopener noreferrer" className="text-[11px] font-bold text-white px-3 py-2 rounded-lg flex items-center gap-1.5 transition-colors" style={{ background: 'var(--st-primary)' }} data-testid="student-attachment-viewer-download">
            <Download className="w-3.5 h-3.5" /> Download
          </a>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/5" style={{ color: 'var(--st-mute)' }} data-testid="student-attachment-viewer-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden flex items-center justify-center relative" style={{ background: 'var(--st-bg)' }}>
          {loading && (
            <div className="flex flex-col items-center gap-3">
              <div className="w-7 h-7 rounded-full animate-spin border-2 border-t-transparent" style={{ borderColor: 'var(--st-primary)', borderTopColor: 'transparent' }} />
              <p className="text-[12px]" style={{ color: 'var(--st-mute)' }}>Loading preview…</p>
            </div>
          )}
          {!loading && error && (
            <div className="text-center p-8">
              <p className="text-[13px] font-semibold text-red-500">{error}</p>
              <a href={directUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold hover:underline" style={{ color: 'var(--st-primary)' }}>
                <Download className="w-3.5 h-3.5" /> Try direct download
              </a>
            </div>
          )}
          {!loading && !error && blobUrl && attachment.category === 'image' && (
            <img src={blobUrl} alt={attachment.title} className="max-w-full max-h-full object-contain shadow-xl" data-testid="student-attachment-viewer-image" />
          )}
          {!loading && !error && blobUrl && attachment.extension === 'pdf' && (
            <iframe src={blobUrl} title={attachment.title} className="w-full h-full bg-white" data-testid="student-attachment-viewer-pdf" />
          )}
          {!loading && !error && blobUrl && attachment.category === 'video' && (
            <video src={blobUrl} controls autoPlay className="max-w-full max-h-full bg-black shadow-xl" data-testid="student-attachment-viewer-video" />
          )}
          {!loading && !error && blobUrl && attachment.category === 'audio' && (
            <div className="flex flex-col items-center gap-5 p-8">
              <div className="w-32 h-32 rounded-3xl flex items-center justify-center shadow-xl" style={{ background: theme.bg, color: theme.text }}>
                <Music className="w-14 h-14" />
              </div>
              <p className="text-[14px] font-bold" style={{ color: 'var(--st-ink)' }}>{attachment.title}</p>
              <audio src={blobUrl} controls autoPlay className="w-96" data-testid="student-attachment-viewer-audio" />
            </div>
          )}
          {!loading && !error && textContent !== null && (
            <pre className="w-full h-full overflow-auto p-6 text-[12px] whitespace-pre-wrap" style={{ color: 'var(--st-ink)', background: 'var(--st-card)' }} data-testid="student-attachment-viewer-text">{textContent}</pre>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
